import type { ChangeEvent } from "react";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Modal } from "components/designV2/modal/modal";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import {
  ClockStampFormArea,
  ClockStampInfoDate,
  ClockStampInfoTime,
  ClockStampInfoTitle,
  ClockStampLayout,
  FormContainer,
  FormStyled,
  InfoContainer,
  InputContainer,
  ModuleInactiveContainer,
  ModuleInactiveIconContainer,
  Text,
  TextInput,
} from "./clockStamp.styled";
import { ClockStampCheckInOutModal } from "./clockStampCheckInOutModal.component";
import { ClockStampChooseTimeReportModal } from "./clockStampChooseTimeReportModal.component";
import { ClockStampCreateAutoGeneratedGigModal } from "./clockStampCreateAutoGeneratedGigModal.component";
import { useClockStamp } from "./clockStamp.logic";
import { ClockStampTimeReportPanel } from "./clockStampTimeReportPanel.component";
import { NumberPad } from "./numberPad.component";

// Keep the field to characters a phone (+46…) or personal number (900101-1234)
// can contain — digits, plus, dash — so letters can't be typed or pasted in.
const sanitizeIdentifierInput = (value: string): string =>
  value.replace(/[^\d+-]/g, "");

export const ClockStamp = (): JSX.Element => {
  const {
    t,
    identifier,
    setIdentifier,
    identifierPlaceholderKey,
    userIsCheckedIn,
    keyShouldBeDisabled,
    modalState,
    today,
    time,
    selectedWorkerTimeReport,
    setSelectedWorkerTimeReport,
    workerTimeReports,
    errorText,
    isAutoGeneratedGig,
    setIsAutoGeneratedGig,
    timeReportDetails,
    setTimeReportDetails,
    stampResult,
    teams,
    timeReportDefaultSubmitData,
    timeReportDeviations,
    isTimeReportLoading,
    isSubmittingTimeReport,
    workerIdentifier,
    setWorkerIdentifier,
    setKeyShouldBeDisabled,
    setSimpleModalOpen,
    setTimeReportModalOpen,
    setWarningModalOpen,
    clearInputs,
    handleCheckInCheckOut,
    handleEditTimeReport,
    handleSubmitDefaultTimeReport,
    submitTimeReport,
    handleNumberPadInput,
    haveStampClockModule,
  } = useClockStamp();

  if (!haveStampClockModule) {
    return (
      <ModuleInactiveContainer>
        <ModuleInactiveIconContainer>
          <CustomIcon
            name="v2/clock"
            color={ColorV2.SimflexityGreen}
            size="100px"
          />
        </ModuleInactiveIconContainer>
        <Text typography="h3" align="center" color={ColorV2.Blue1}>
          {t("StampClock.Oops")}
        </Text>
        <Text typography="b1" align="center" color={ColorV2.Blue1}>
          {t("StampClock.ModuleInactive")}
        </Text>
        <Text typography="b1" align="center" color={ColorV2.Blue1}>
          {t("StampClock.YourAdminCanActivate")}
        </Text>
      </ModuleInactiveContainer>
    );
  }

  return (
    <>
      <ClockStampLayout>
        <ClockStampFormArea>
          <FormContainer>
            <FormStyled>
              {errorText !== "" && (
                <Text typography="b1" color={ColorV2.NegativeRed}>
                  {t(errorText)}
                </Text>
              )}

              <InputContainer>
                <TextInput
                  value={identifier}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setIdentifier(sanitizeIdentifierInput(event.target.value))
                  }
                  placeholder={t(identifierPlaceholderKey)}
                />
              </InputContainer>

              <NumberPad
                checkedIn={userIsCheckedIn}
                keyShouldBeDisabled={keyShouldBeDisabled}
                onNumberChange={handleNumberPadInput}
                onClickStampKey={handleCheckInCheckOut}
              />
            </FormStyled>
          </FormContainer>
        </ClockStampFormArea>

        <InfoContainer>
          <ClockStampInfoTitle>{t("StampClock.Welcome")}</ClockStampInfoTitle>
          <ClockStampInfoTime>{time}</ClockStampInfoTime>
          <ClockStampInfoDate>{today}</ClockStampInfoDate>
        </InfoContainer>
      </ClockStampLayout>

      {modalState.type === "checkInOut" && selectedWorkerTimeReport && (
        <ClockStampCheckInOutModal
          timeReport={selectedWorkerTimeReport}
          stampResult={stampResult}
          setShowModal={setSimpleModalOpen("checkInOut")}
          setKeyShouldBeDisabled={setKeyShouldBeDisabled}
          showModal={modalState.type === "checkInOut"}
          isAutoGeneratedGig={isAutoGeneratedGig}
          setIsAutoGeneratedGig={setIsAutoGeneratedGig}
          handleEditTimeReport={handleEditTimeReport}
          handleSubmitDefaultTimeReport={handleSubmitDefaultTimeReport}
          timeReportDefaultSubmitData={timeReportDefaultSubmitData}
          timeReportDeviations={timeReportDeviations}
          hasTimeReport={timeReportDetails != null}
          isTimeReportLoading={isTimeReportLoading}
          isSubmittingTimeReport={isSubmittingTimeReport}
          clearInputs={clearInputs}
        />
      )}

      {modalState.type === "createAutoGeneratedGig" && (
        <ClockStampCreateAutoGeneratedGigModal
          showCreateAutoGeneratedGigModal={
            modalState.type === "createAutoGeneratedGig"
          }
          setShowCreateAutoGeneratedGigModal={setSimpleModalOpen(
            "createAutoGeneratedGig",
          )}
          setKeyShouldBeDisabled={setKeyShouldBeDisabled}
          setTimeReport={setSelectedWorkerTimeReport}
          setShowModal={setSimpleModalOpen("checkInOut")}
          setIsAutoGeneratedGig={setIsAutoGeneratedGig}
          clearInputs={clearInputs}
          teams={teams}
          workerIdentifier={workerIdentifier}
        />
      )}

      {modalState.type === "timeReport" && timeReportDetails && (
        <ClockStampTimeReportPanel
          isOpen={modalState.type === "timeReport"}
          timeReport={timeReportDetails}
          onBackdropClick={() => setWarningModalOpen(true)}
          onClose={() => {
            setTimeReportModalOpen(false);
            setTimeReportDetails(null);
            setWorkerIdentifier("");
            setKeyShouldBeDisabled(false);
          }}
          onSubmit={submitTimeReport}
          isSubmitting={isSubmittingTimeReport}
        />
      )}

      {modalState.type === "chooseTimeReport" && (
        <ClockStampChooseTimeReportModal
          setShowChooseTimeReportModal={setSimpleModalOpen("chooseTimeReport")}
          setKeyShouldBeDisabled={setKeyShouldBeDisabled}
          showChooseTimeReportModal={modalState.type === "chooseTimeReport"}
          setTimeReport={setSelectedWorkerTimeReport}
          clearInputs={clearInputs}
          workerTimeReports={workerTimeReports}
          handleCheckInCheckOut={handleCheckInCheckOut}
          timeReport={selectedWorkerTimeReport}
        />
      )}
      <Modal
        isOpen={modalState.type === "timeReport" && modalState.showWarningModal}
        onClose={() => {
          setWarningModalOpen(false);
        }}
        label={t("StampClock.CancelTimeReporting")}
        size="compact"
        variant="confirm"
        showCloseButton={false}
        footer={
          <>
            <SecondaryButton
              label={t("General.No")}
              onClick={() => {
                setWarningModalOpen(false);
              }}
            />
            <PrimaryButton
              label={t("General.Yes")}
              onClick={() => {
                setTimeReportModalOpen(false);
              }}
            />
          </>
        }
      >
        {t("StampClock.AreYourSureYouDontWantToTimeReport")}
      </Modal>
    </>
  );
};
